import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { useEffect, useRef } from 'react';

import { useActiveChatStore } from '#modules/chat/model/activeChatStore';
import { resolveChatPreviewKind } from '#modules/chat/utils/systemMessage';
import {
  GameLogo,
  SessionProgressBar,
} from '#modules/finder/features/posts/components/SessionParts';
import { trackRoomChatOpened } from '#features/analytics/utils/sessionRoomFunnel';
import { CORNER_PROPS } from '#features/media-window/model';
import { useCornerDrag } from '#features/media-window/useCornerDrag';
import { Button, CommentIcon, UnreadBadge } from '#features/ui';
import { IconButton } from '#features/ui/components/IconButton';
import { Tooltip } from '#features/ui/components/Tooltip';
import { SessionVoiceTier } from '#features/voice/components/SessionVoiceTier';
import { useVoiceRoomState } from '#features/voice/hooks/useVoiceRoomState';
import { useRoomSystemPhrase } from '../hooks/useRoomSystemPhrase';
import type { LastSystemActor, SessionRoomEntry } from '../model/roomSessions';
import { useSessionRoomStore } from '../model/sessionRoomStore';
import {
  hasOpenDismissableLayer,
  isClosedPhase,
  isTextEntry,
  resolveEscapeAction,
  resolvePreview,
  showsVoiceTier,
  type SessionRoomPhase,
} from './RoomChrome';
import { RoomDeviceButton } from './RoomDeviceButton';
import { RoomGrip } from './RoomGrip';
import { RoomModeButton } from './RoomModeButton';
import { RoomPhaseLabel } from './RoomPhaseLabel';
import { RoomSurface } from './RoomSurface';
import { SessionRoomMenu } from './SessionRoomMenu';

/**
 * Комната сессии окном: шапка, ярус голоса и настоящая переписка с полем ввода – в одном
 * предмете, без переходов.
 *
 * Одна коробка, а не стопка «голос сверху, чат снизу»: сессия – это один момент, когда люди
 * одновременно слышат друг друга и пишут, и делить его на два плавающих окна в одном углу значит
 * заставлять человека управлять двумя предметами ради одного дела.
 *
 * Ширина, угол, тень и радиус – те же, что у окна разговора 1:1 в `CallOverlay`: два окна одной
 * природы обязаны выглядеть роднёй. «×» здесь нет до самого конца сессии – закрытие означало бы
 * выход из неё, а это отдельное действие с подтверждением, оно живёт в кебабе.
 */

/** Ширина – та же, что у окна звонка 1:1. */
const WINDOW_WIDTH = '400px';
/**
 * Верхняя полоса экрана, в которую окно не заходит.
 *
 * Наверху у каждой страницы приложения стоят её органы управления: шапка приложения (60px плюс
 * безопасная зона) и под ней собственная шапка страницы – в чужом треде это 52px с «Позвонить» и
 * «Меню чата». Растущее вверх окно доходило до них и накрывало нижние пиксели кнопок: нажатие
 * попадало в окно, а человек целился в чат, поверх которого оно стоит.
 *
 * Порог – не одна из двух величин, а большая из них: точной высоты чужой шапки окно не знает
 * (над ней бывает ещё и полоса «поставьте приложение»), а на высоком экране пятая часть экрана
 * честно описывает всё, что там обычно живёт. Меньше 128px порог не опускается – это шапка
 * приложения и шапка страницы, и они стоят на любом экране.
 */
export const TOP_CLEARANCE = `max(calc(60px + env(safe-area-inset-top, 0px) + 52px + var(--chakra-spacing-4)), 20dvh)`;
/**
 * Потолок считается от отступов, а не глухими 640: окно из нижнего угла растёт вверх, и на
 * невысоком экране высокая коробка выдавила бы собственную шапку за кромку – вместе с кнопкой
 * «свернуть», то есть вернуть окно было бы нечем.
 */
export const WINDOW_MAX_HEIGHT = `min(640px, calc(100dvh - var(--chakra-spacing-4) - ${TOP_CLEARANCE}))`;

/** Полоска-ручка сверху: за неё окно таскают по углам. */
const GRIP_HEIGHT = '14px';
/** Цель нажатия в строке яруса, как у голосовых кнопок рядом. */
const TIER_ACTION = '40px';

type SessionRoomWindowProps = {
  session: SessionRoomEntry;
  phase: SessionRoomPhase;
  /**
   * Свернуться в пилюлю. Режим комнаты живёт в её сторе, поэтому обработчик необязателен: без
   * него окно сворачивается само.
   */
  onCollapse?: () => void;
  /**
   * Уйти на весь экран – на маршрут треда. `null`, пока переписки нет: кнопке нечего открывать,
   * и она стоит выключенной с подписью «Чат создаётся…».
   */
  onFullscreen?: (() => void) | null;
  /** Закрыть плашку прощания. Есть только в `ended`: до конца сессии «×» нет. */
  onClose?: () => void;
  /**
   * Автор последней реплики. Запрос комнаты имени не отдаёт, и подставлять сюда чужое имя хуже,
   * чем показать одну реплику: имя приходит от того, кто держит ленту.
   */
  lastMessageAuthor?: string | null;
  /** О ком последняя служебная строка – см. `SessionRoomSurfaceProps`. */
  lastSystemActor?: LastSystemActor | null;
  /** Открыть `SessionDetailsDialog`: он живёт у хоста, а не внутри поверхности. */
  onOpenSessionSettings?: () => void;
  /** Открыть «Позвать в сессию» – диалог у хоста, как и настройки. */
  onOpenInvite?: () => void;
  /** Спросить подтверждение и выйти из сессии. Без обработчика спрашивает сам кебаб. */
  onLeave?: () => void;
  /**
   * Открыть список устройств из кебаба.
   *
   * Свой экземпляр `VoiceDevicePicker` здесь завести нельзя: переключение устройства работает
   * только у того, кто владеет соединением LiveKit (`useVoiceRoom` – синглтон), а владеет им
   * ярус голоса. Второй список молча не переключал бы микрофон – хуже, чем недоступный пункт.
   */
  onAudioSettings?: () => void;
  /**
   * Окно только что развернули из пилюли – забрать фокус себе.
   *
   * Разворачивание из пилюли оставляло фокус на `body`, и обещанный брифом Escape («свернуть»)
   * с клавиатуры был недостижим: обработчик висит на коробке окна, а нажатие до неё не доходило.
   * Само появление окна фокус не забирает: комната монтируется на загрузке страницы, и отнимать
   * фокус у того, кто её открыл, не за что.
   */
  autoFocus?: boolean;
};

export const SessionRoomWindow = ({
  session,
  phase,
  onCollapse,
  onFullscreen,
  onClose,
  onOpenSessionSettings,
  onOpenInvite,
  onLeave,
  onAudioSettings,
  autoFocus = false,
  lastMessageAuthor,
  lastSystemActor,
}: SessionRoomWindowProps) => {
  const { t } = useLingui();
  /*
    Превью считает та же функция, что в пилюле: одна комната в двух плотностях обязана называть
    последнюю реплику одинаково, включая служебные строки вроде «сессия создана».
  */
  const isChatDeleted = useActiveChatStore((store) => store.isChatDeleted(session.chatId));
  const preview = resolvePreview({
    chatId: session.chatId,
    isChatDeleted,
    lastMessageText: session.lastMessageText,
    lastMessageAuthor,
  });

  const systemPhrase = useRoomSystemPhrase();

  /*
    Служебная строка приезжает ПРИЗНАКОМ события (`SESSION_STARTED`), а не готовым текстом:
    переводить её обязан читатель, иначе строка осталась бы на языке того, кто её вызвал. Без
    этого разбора окно писало сырое слово сервера – ровно то, что пилюля научилась не делать
    ещё 09.09, а сюда строка приехала только 12.09 и приём за собой не принесла.
  */
  const previewSystemKind =
    preview.kind === 'message' ? resolveChatPreviewKind(preview.text) : null;

  const corner = useSessionRoomStore((state) => state.corner);
  const setCorner = useSessionRoomStore((state) => state.setCorner);
  const setMode = useSessionRoomStore((state) => state.setMode);
  const voiceListOpen = useSessionRoomStore((state) => state.voiceListOpen);
  const setVoiceListOpen = useSessionRoomStore((state) => state.setVoiceListOpen);

  const windowRef = useRef<HTMLDivElement | null>(null);

  /** Свернуться в пилюлю: режим комнаты живёт в её сторе, а не в пропах поверхности. */
  const collapse = () => (onCollapse ? onCollapse() : setMode('mini'));

  /*
    Развернули из пилюли – фокус переезжает на коробку окна. Не в композер: набор текста человек
    начинает сам, а вот управлять только что открытым предметом с клавиатуры он вправе сразу –
    Escape свернёт, Tab уйдёт по кнопкам шапки. `preventScroll` обязателен: окно закреплено, и
    браузер, доводя его до видимой части, дёрнул бы прокрутку страницы под ним.
  */
  useEffect(() => {
    if (autoFocus) {
      windowRef.current?.focus({ preventScroll: true });
    }
  }, [autoFocus]);

  const onGripPointerDown = useCornerDrag({
    ref: windowRef,
    canDrag: true,
    onCollapse: collapse,
    onCorner: setCorner,
  });

  const {
    inThisRoom,
    isPlatform,
    state: voiceState,
    roomStatus,
  } = useVoiceRoomState({
    sessionId: session.sessionId,
    voiceMode: session.voiceMode,
    teamLimit: session.teamLimit ?? 0,
  });

  const closed = isClosedPhase(phase);
  const voiceTierVisible = showsVoiceTier(
    phase,
    inThisRoom,
    voiceState,
    roomStatus ?? session.voiceRoomStatus
  );
  const progress =
    phase === 'live' && session.startedAt && session.expiredAt
      ? { startedAt: session.startedAt, expiredAt: session.expiredAt }
      : null;

  return (
    <RoomSurface
      ref={windowRef}
      data-testid="session-room-window"
      /*
        Коробка сама принимает фокус. Escape ловит она, и пока фокус мог уйти на `body` – из
        композера или после разворачивания, – нажатие до неё не доходило и окно не сворачивалось.
        Только программно (`-1`): в обход Tab, чтобы не появляться лишней остановкой между
        кнопками страницы.
      */
      tabIndex={-1}
      aria-label={t`post:session.room.windowAria`}
      {...CORNER_PROPS[corner]}
      w={WINDOW_WIDTH}
      /*
        Раскрытый состав сначала растит окно до потолка и только потом ужимает ленту: пять строк
        аккордеона, отобранные у переписки, оставили бы от неё одно сообщение.
      */
      /*
        Высота по содержимому: без ленты окну нечего растягивать. Шапка, ярус голоса и кнопка
        занимают около 170px, раскрытый состав добавляет свои строки – и упирается в потолок,
        дальше список прокручивается сам.
      */
      h="auto"
      maxH={WINDOW_MAX_HEIGHT}
      direction="column"
      overflow="hidden"
      animation="fadeIn var(--chakra-durations-moderate) var(--chakra-easings-ease-in-smooth)"
      css={{
        overscrollBehavior: 'none',
        // Фокус на самой коробке служебный, его ставит код; рамка на нём читалась бы как
        // выбранный человеком элемент. У кнопок внутри focus-visible работает как обычно.
        '&:focus': { outline: 'none' },
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
      onKeyDown={(event) => {
        /*
          Escape при фокусе внутри окна сворачивает его в пилюлю, а не закрывает: закрытия у
          комнаты нет до конца сессии. Слушаем на самой коробке, а не на документе, – иначе
          Escape в чужом диалоге поверх окна тоже сворачивал бы комнату.

          Но сворачиваем только когда закрывать больше нечего: внутри окна живут поповер
          устройств, кебаб, эмодзи-пикер и композер, и там Escape означает «закрой меня». Правило
          целиком – в `resolveEscapeAction`.
        */
        if (event.key !== 'Escape') {
          return;
        }

        const target = event.target;
        const field = isTextEntry(target) ? target : null;
        const action = resolveEscapeAction({
          defaultPrevented: event.defaultPrevented,
          composing: event.nativeEvent.isComposing,
          layerOpen: hasOpenDismissableLayer(event.currentTarget.ownerDocument),
          inTextField: field !== null,
        });

        if (action === 'ignore') {
          return;
        }

        // Нажатие потрачено здесь: ни выход из поля, ни сворачивание не должны достаться ещё и
        // соседнему обработчику выше по дереву.
        event.stopPropagation();

        if (action === 'leaveField') {
          /*
            Из поля фокус переезжает на коробку окна, а не в никуда. `blur()` отправлял его на
            `body`, и второе нажатие Escape окно уже не сворачивало: обработчик висит здесь, а
            фокус его покинул – обещанное брифом «Escape сворачивает» с клавиатуры не работало.
            Фокус на коробке сам снимает его с поля, поэтому `blur()` не нужен.
          */
          event.currentTarget.focus({ preventScroll: true });

          return;
        }

        collapse();
      }}
    >
      {/* Ручка отдельным элементом, а не всей шапкой: жест на шапке отнимал бы нажатия у кнопок,
          которые в ней стоят. */}
      <Box
        data-testid="voice-window-grip"
        h={GRIP_HEIGHT}
        flexShrink={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        cursor="grab"
        userSelect="none"
        onPointerDown={onGripPointerDown}
        css={{ touchAction: 'none', overscrollBehavior: 'contain' }}
      >
        <RoomGrip />
      </Box>

      <Flex pl={4} pr={2} py={2} gap={2} align="center" flexShrink={0}>
        <Box flex={1} minW={0}>
          <Flex align="center" gap={2} minW={0}>
            {session.game?.icon ? (
              <GameLogo icon={session.game.icon} color={session.game.iconColor} diameter={24} />
            ) : null}
            <Text textStyle="title" lineClamp={1} minW={0}>
              {session.game?.name
                ? t`post:session.room.gameSessionTitle ${session.game.name}`
                : t`post:session.room.sessionTitle`}
            </Text>
          </Flex>
          <RoomPhaseLabel
            phase={phase}
            startedAt={session.startedAt}
            expiredAt={session.expiredAt}
          />
        </Box>
        <Flex gap={1} flexShrink={0} align="center">
          {/* Предварительный выбор устройств на поддерживаемых платформах. */}
          {isPlatform && voiceTierVisible ? (
            <RoomDeviceButton inThisRoom={inThisRoom} size="40px" />
          ) : null}
          <RoomModeButton mode="collapse" onClick={collapse} />
          <SessionRoomMenu
            postId={session.postId}
            sessionId={session.sessionId}
            hostId={session.hostId}
            phase={phase}
            inVoice={inThisRoom}
            chatId={session.chatId}
            startedAt={session.startedAt}
            expiredAt={session.expiredAt}
            onSessionSettings={onOpenSessionSettings}
            onAudioSettings={isPlatform ? onAudioSettings : undefined}
            onLeave={onLeave}
          />
        </Flex>
      </Flex>

      {/* Низ шапки: у идущей сессии – прогресс, иначе волосяная линия. Высота одна и та же,
          поэтому шапка не дёргается на старте и в конце. */}
      {progress ? (
        <SessionProgressBar {...progress} height="2px" />
      ) : (
        <Box h="2px" flexShrink={0} borderBottomWidth="1px" borderColor="border" />
      )}

      {/* [Ярус-сцена 16:9] – фаза 3. В DOM его нет, пока в комнате нет живого видео. */}

      {/*
        Разговор переживает сессию: вход остаётся пока комната открыта, микрофон и выход -
        пока я подключён.
      */}
      {voiceTierVisible ? (
        <SessionVoiceTier
          sessionId={session.sessionId}
          postId={session.postId}
          expanded={voiceListOpen}
          onExpandedChange={setVoiceListOpen}
          /*
            Звать больше некуда: сессия кончилась. Ярус голоса после её конца остаётся, пока я в
            голосе (23.09.2026), и вместе с ним оставалась кнопка «Пригласить» - она вела в
            диалог, который позвал бы людей в то, чего уже нет (смоук 26.09.2026).
          */
          onInvite={closed ? undefined : onOpenInvite}
          /*
            Вход в переписку стоит в той же строке, что голос, и значком, а не кнопкой во всю
            ширину. Обе двери ведут в разговор и весят одинаково, а целая строка под одну из них
            делала окно на 44px выше ради кнопки, которую нажимают реже, чем смотрят на голос.
          */
          actions={
            /*
              Кнопка стоит всегда, даже пока переписки нет: раньше она при этом исчезала целиком,
              и подсказка «Чат создаётся…» – единственное объяснение – оказывалась внутри ветки,
              до которой в этом случае не доходили. Человек видел пустое место и читал его как
              «чата у этой сессии не будет».
            */
            <Tooltip
              content={t`post:session.room.chatCreating`}
              disabled={Boolean(onFullscreen)}
              openDelay={200}
            >
              <Box position="relative" flexShrink={0}>
                <IconButton
                  variant="ghost"
                  aria-label={onFullscreen ? t`Open chat` : t`post:session.room.chatCreating`}
                  w={TIER_ACTION}
                  minW={TIER_ACTION}
                  h={TIER_ACTION}
                  color="fg.icon"
                  // Переписки ещё нет – вести некуда. Кнопка остаётся: пустое место на её месте
                  // читалось бы как «чата у этой сессии не будет».
                  disabled={!onFullscreen}
                  onClick={() => {
                    // Дверь в переписку - числитель замера комнаты. Их три, и считаются все три.
                    trackRoomChatOpened('window');
                    onFullscreen?.();
                  }}
                >
                  {onFullscreen ? <CommentIcon /> : <Spinner size="sm" />}
                </IconButton>
                {session.unreadCount > 0 ? (
                  <Box position="absolute" top="2px" right="2px" pointerEvents="none">
                    <UnreadBadge count={session.unreadCount} size="sm" />
                  </Box>
                ) : null}
              </Box>
            </Tooltip>
          }
        />
      ) : null}

      {/*
        Последняя реплика строкой под голосом – ровно то же, что во второй строке пилюли, и по
        той же причине: главный вопрос к окну в углу не «кто в голосе», а «нужно ли мне туда
        смотреть», и непрочитанная реплика отвечает на него точнее счётчика.

        Появляется только когда есть что показать: без переписки, у пустой ленты и после
        удаления чата строки нет вовсе, и окно возвращается к своей высоте.
      */}
      {!closed && preview.kind === 'message' ? (
        <Text
          px={4}
          pb={3}
          pt={2}
          borderTopWidth="1px"
          borderColor="border.subtle"
          textStyle="caption"
          color={previewSystemKind ? 'fg.muted' : 'fg.secondary'}
          lineClamp={1}
          flexShrink={0}
        >
          {previewSystemKind ? (
            systemPhrase(previewSystemKind, lastSystemActor, inThisRoom)
          ) : (
            <>
              {preview.name ? (
                <>
                  <Box as="span" color="fg.emphasized" textStyle="label">
                    {preview.name}:
                  </Box>{' '}
                </>
              ) : null}
              {preview.kind === 'message' ? preview.text : null}
            </>
          )}
        </Text>
      ) : null}

      {closed && !voiceTierVisible ? (
        /*
          Дверь в переписку у закончившейся сессии – здесь, рядом с «Закрыть». С 12.09.2026 вход в
          тред стоит значком в строке голоса, а голоса у закончившейся сессии нет, и строка не
          рисуется: окно говорило «чат остаётся» и не давало в него попасть.
        */
        <Flex px={4} py={3} gap={2} flexShrink={0}>
          {session.chatId && onFullscreen ? (
            phase === 'cancelled' ? (
              <Tooltip content={t`Open chat`}>
                <IconButton
                  variant="outline"
                  aria-label={t`Open chat`}
                  size="sm"
                  minW={11}
                  minH={11}
                  onClick={() => {
                    trackRoomChatOpened('window');
                    onFullscreen();
                  }}
                >
                  <CommentIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                variant="outline"
                size="sm"
                flex={1}
                css={{ '@media (any-pointer: coarse)': { minHeight: '44px' } }}
                onClick={() => {
                  trackRoomChatOpened('window');
                  onFullscreen();
                }}
              >
                {t`Open chat`}
              </Button>
            )
          ) : null}
          {/*
            Пока я в голосе, закрыть комнату нельзя: вместе с ней ушли бы микрофон и выход, а
            меня по-прежнему слышно. Выход из голоса - в кебабе и в ярусе выше.
          */}
          {inThisRoom ? null : (
            <Button
              variant="outline"
              size="sm"
              flex={1}
              css={{ '@media (any-pointer: coarse)': { minHeight: '44px' } }}
              onClick={onClose}
            >
              {t`post:session.room.close`}
            </Button>
          )}
        </Flex>
      ) : null}
    </RoomSurface>
  );
};
